import { useId } from 'react';

import { inputVariants } from '../ds';
import { c, mono } from '../tokens';

/**
 * Repository name field with a locked `cdp-` prefix.
 *
 * Every portal-created repo must be `cdp-*`: the prefix is what
 * `server/src/apps.ts` filters the catalog on, what `onboarding-status` validates
 * against, and (per pilot-infra) the pattern the WIF binding can be tightened to.
 * So the user edits the suffix only — the prefix is rendered as static text
 * outside the editable area, not as pre-filled input content they can delete.
 *
 * The DS `Input` is a bare `<input>` with no adornment slot, so this composes the
 * shell from `inputVariants()` — the same cva the DS's own `Select` and
 * `Combobox` use to make a wrapper look exactly like a text input. That keeps the
 * border, radius, height, focus and invalid states identical to a real `Input`
 * without duplicating a single class name.
 *
 * The server enforces this independently (`checkRepoName` rejects anything not
 * matching `cdp-*`), so this is UX, not the security boundary.
 */

export const REPO_PREFIX = 'cdp-';

/** Everything a valid repo name allows after the prefix. */
const SUFFIX_ALLOWED = /[^a-z0-9-]/g;

/**
 * Normalize whatever lands in the field — typed, pasted, or autofilled — into a
 * legal suffix.
 *
 * Order matters here:
 *   1. whitespace and underscores become hyphens, so typing "my app" gives
 *      "my-app" rather than silently dropping the space. This runs BEFORE the
 *      prefix strip so "cdp another" is handled too.
 *   2. a leading `cdp-` is removed, however many times it appears — pasting the
 *      full repo name is the obvious thing to do and would otherwise yield
 *      `cdp-cdp-my-app`.
 *   3. anything still illegal is dropped, then hyphen runs collapse.
 */
export function normalizeSuffix(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/^(cdp-)+/, '')
    .replace(SUFFIX_ALLOWED, '')
    .replace(/-{2,}/g, '-');
}

/**
 * Derive the default suffix from the app name. Unlike the typing path this can
 * trim edge hyphens, because an app name is a complete value rather than
 * something mid-keystroke.
 */
export function suffixFromAppName(appName: string): string {
  return normalizeSuffix(appName.trim()).replace(/^-+|-+$/g, '');
}

/** A repo name is only valid if the suffix is non-empty and ends alphanumerically. */
export function suffixError(suffix: string): string | undefined {
  if (suffix.length === 0) return 'Add a name after the cdp- prefix.';
  if (suffix.startsWith('-')) return 'Cannot start with a hyphen.';
  if (suffix.endsWith('-')) return 'Cannot end with a hyphen.';
  return undefined;
}

interface RepoNameFieldProps {
  suffix: string;
  onSuffixChange: (suffix: string) => void;
  invalid?: boolean;
  id?: string;
}

export function RepoNameField({ suffix, onSuffixChange, invalid, id }: RepoNameFieldProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div
      className={inputVariants({ size: 'md', invalid })}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        // The shell owns the padding so the prefix sits inside the border; the
        // inner input then runs flush to it.
        paddingRight: 0,
        cursor: 'text',
      }}
      onClick={() => document.getElementById(inputId)?.focus()}
    >
      <span
        aria-hidden="true"
        style={{
          fontFamily: mono,
          fontSize: 14,
          color: c.textFaint,
          userSelect: 'none',
          flex: '0 0 auto',
        }}
      >
        {REPO_PREFIX}
      </span>
      <input
        id={inputId}
        value={suffix}
        onChange={event => onSuffixChange(normalizeSuffix(event.target.value))}
        placeholder="visit-summary-exporter"
        spellCheck={false}
        autoCapitalize="none"
        autoComplete="off"
        aria-describedby={`${inputId}-prefix-note`}
        style={{
          // Deliberately unstyled: the wrapper above already carries the DS's
          // border, height, radius and focus treatment.
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontFamily: mono,
          fontSize: 14,
          color: c.text,
          padding: 0,
        }}
      />
      <span id={`${inputId}-prefix-note`} style={{ position: 'absolute', left: -9999 }}>
        Repository names always begin with {REPO_PREFIX} and this cannot be changed.
      </span>
    </div>
  );
}
